/**
 * OTA live updates — the app-side controller of `@capgo/capacitor-updater`
 * (self-hosted; plan docs/speak-plan/49-ota-live-updates.md § 4, ported from Club
 * Arena's `apps/web/src/lib/native/ota.ts`, whose plan § 4.5–4.6 and § 7 it follows).
 * No-op on the web and on any binary without the plugin.
 *
 * The plugin runs in `autoUpdate: 'onlyDownload'` (apps/speak/capacitor.config.ts):
 * it asks our endpoint, downloads and verifies a signed bundle, emits
 * `updateAvailable`, and never stages nor applies anything by itself. This
 * module decides the rest:
 *
 * 1. `markOtaAppReady()` — `notifyAppReady()`, called by `App` as soon as the React
 *    root has committed its first render. NEVER after a network call: a new bundle
 *    that does not confirm within `appReadyTimeout` (30 s) is rolled back, and tying
 *    the confirmation to the network (the auth hydrate, for one) would roll back
 *    users who are offline or on a slow line.
 *
 * 2. When a bundle goes live — never in the middle of something. Applying a bundle
 *    reloads the WebView. Whatever a reload must not cut in half takes a HOLD with
 *    `holdOtaApply(reason)` and calls the returned `release()` when done
 *    (`useOtaApplyHold` is the React form). Speak's holds (plan § 4):
 *      - `voice-session`: a voice conversation connecting or live, until it closes
 *        (screens/conversation/useConversationController.ts);
 *      - `listen-playback`: /listen audio playing (screens/listen/ListenProvider.tsx);
 *      - `iap-purchase`: a RevenueCat purchase from the request until it settles
 *        (billing/AddCreditsSheet.tsx) — the store sheet sends the app to the
 *        background on Android, which is exactly when the plugin applies;
 *      - `mic-recording`: any other mic capture (lib/micWavCapture.ts: A0
 *        repeat-check, pronunciation drills).
 *    Holds are in memory: a reload or a kill drops them, which is fine — whatever
 *    they protected died with the page too. A forgotten release only delays the
 *    update to the next cold start, never loses it.
 *
 *    THE DECISION lives in one function, `syncApply()`, and nowhere else:
 *      - nothing holding → stage the downloaded bundle with `next()` and make sure
 *        no delay condition holds it (`cancelDelay()`): the plugin applies a staged
 *        bundle the next time the app goes to the BACKGROUND, so the reload happens
 *        while the user is not looking, and `keepUrlPathAfterReload` brings them
 *        back to the same screen;
 *      - a hold taken → a bundle that is not staged yet stays pending (it is staged
 *        when the last hold is released), and a staged one is HELD with a `kill`
 *        delay condition, so backgrounding the app applies nothing. The hold is
 *        released the moment no hold is taken.
 *      - `set()` and `reload()` are never called: nothing is applied while the
 *        app is visible.
 *    If the user never backgrounds the app with nothing holding, the bundle waits:
 *    it goes live in the first background after the next cold start.
 *
 * 2b. A download that a newer one replaced before it ran is deleted from disk
 *    (`pruneSuperseded()`, after every download and at launch): the plugin keeps it
 *    otherwise, a whole web dir per version.
 *
 *    What Club Arena read in the plugin source (8.53.0) and proved on devices, both
 *    platforms (its plan § 8): `next()` only marks the bundle; `appMovedToBackground`
 *    → `installNext()` applies it unless delay conditions are pending; a `kill`
 *    condition survives background/foreground cycles and is cleared at the next
 *    launch; a cold start does NOT apply the staged bundle by itself (it waits for
 *    the next background). If a plugin bump changes any of that, change
 *    `syncApply()` only.
 *
 * 3. Rollback reporting — a bundle that never confirmed is reverted by the
 *    plugin, which fires `updateFailed` (inside the failing bundle, if its JS
 *    runs at all) and remembers it, once, for `getFailedUpdate()`. Both paths
 *    report to ONE Sentry issue per failed bundle version (tag `surface=ota`,
 *    fingerprint by version). The launch path is the one that counts: the
 *    event's send usually dies with the reload that follows it, so only the
 *    launch marks the failure as reported (localStorage) — see
 *    `reportUpdateFailed()`.
 *
 * 3b. Failed-version memory — `autoDeleteFailed` deletes a failed bundle but
 *    does NOT stop the plugin from downloading the same version again: the
 *    endpoint kept offering it and the device re-downloaded the full bundle on
 *    every launch and every return to the foreground (measured by Club Arena on
 *    Android for a bad signature, a tampered zip and a bundle that never
 *    confirmed). So the device remembers the versions that failed on it and
 *    tells the endpoint, which stops offering them (Club Arena plan § 4.6, § 14.3):
 *      - a version is recorded when it is rolled back (`updateFailed` event,
 *        `getFailedUpdate()` at launch) or when its ZIP is REJECTED AFTER A
 *        FULL DOWNLOAD (`downloadFailed` for a version whose `download`
 *        progress already reached the "whole zip on disk" mark — 71 on
 *        Android, 70 on iOS —, i.e. decryption, signature, checksum or unzip
 *        failed). A transport failure (the download died before that mark) is
 *        NOT recorded: retrying it is right, and blocking a good version for a
 *        flaky line is not;
 *      - the list (5 most recent, versions only) lives in localStorage and is
 *        handed to the plugin as `custom_id` = `ota-failed:<v1>,<v2>…` with
 *        `setCustomId()`. `persistCustomId: true` (capacitor.config.ts) makes
 *        the plugin store it natively and load it in its own `load()`, so even
 *        the cold-start check — which runs before this JavaScript — carries it;
 *      - nothing identifying the user goes in it, only bundle versions;
 *      - a version that later runs with status `success` is dropped from the
 *        list. Lower versions are kept (a `repoint` rollback serves a LOWER
 *        version, and a version that failed here must stay blocked).
 *    A rollback by republish publishes the old content under a NEW, higher
 *    version, so it is never blocked by this list.
 *
 * 3c. Delta downloads (Club Arena plan § 4.8) — the endpoint normally answers a
 *    per-file list: the plugin copies the files it already has (builtin bundle,
 *    delta cache) and downloads only the rest, each file signed and verified on its
 *    own. When such a download fails, the plugin does NOT say why: a network
 *    error and a file that failed verification emit the same `downloadFailed`
 *    (Android: error "One or more files failed to download"; iOS: no error at
 *    all), and the progress never reaches the zip's "whole download on disk"
 *    mark. So a delta failure is never recorded as a failed version. Instead:
 *      - the retry is cheap (every file that verified is in the delta cache),
 *        so after the FIRST delta failure of a version the next check simply
 *        tries again;
 *      - after the SECOND one — or after the first when the failure cannot be
 *        transient: the plugin refused the per-file list itself before
 *        downloading anything, Android errors in DETERMINISTIC_DELTA_ERRORS —
 *        the app asks the endpoint for the ZIP of that version (`ota-zip:<v>`
 *        in custom_id, after the failed list). The zip is then judged by the
 *        exact rule above: rejected after a full download → recorded as failed;
 *        a transport failure → retried. A version whose delta files were
 *        tampered with but whose zip is intact therefore still installs from
 *        the zip (both are signed independently);
 *      - the counter (one version at a time, localStorage) is dropped when the
 *        version downloads, runs, or is recorded as failed.
 *    On Android a failed attempt that reached 71 is a zip whatever the state
 *    (a delta never emits 71 before failing); on iOS the 70 mark is only
 *    trusted for a version the app asked as a zip (a delta's progress is
 *    capped at 70 too).
 *
 * 3d. Downloaded, waiting to apply (`ota-have:<v>`) — while a downloaded
 *    bundle waits (a hold, or simply until the next background), every check
 *    (each foreground, every 10 min) would get the whole update answer again
 *    (~200 KB gzipped with the per-file list). The plugin does not download the
 *    version again (it finds it on disk: Android `backgroundDownload()` "Latest
 *    bundle already exists and download is NOT required", iOS
 *    `getBundleInfoByVersionName` + `shouldRetryDownloadForExistingBundle`), it
 *    only re-announces it — so the device tells the endpoint which version it holds
 *    and the endpoint answers "no update" for it:
 *      - set when the plugin reports the version downloaded AND verified
 *        (`updateAvailable`, emitted after decrypt + checksum + unzip, bundle
 *        saved as `pending`); one version at a time, a newer download
 *        replaces it. Stored with the native build it was downloaded under;
 *      - dropped when that version runs (any status: the endpoint already says
 *        "up to date" to a device on the live version, and a rollback records
 *        it as failed), when it is recorded as failed, and at launch when the
 *        plugin no longer holds it: the native build changed (`resetWhenUpdate`
 *        discards every OTA bundle after a store update, in a cleanup thread,
 *        with NO event to JavaScript) or `list()` has no usable bundle of that
 *        version. Until then the endpoint may answer "no update" for it — it
 *        honours `ota-have` only when it equals the LIVE version, so a stale
 *        one never blocks a newer release, and costs at most the checks until
 *        the next launch (in practice one: the discard itself happens at a
 *        launch, whose cold-start check runs before this JavaScript);
 *      - at launch the held bundle becomes `pending` again if it is not the
 *        staged one: the endpoint will not announce it any more, so the
 *        controller must stage it from its own memory;
 *      - Android re-announces a held version it was offered again with
 *        `updateAvailable` followed by a `downloadFailed` without error text
 *        (`endBackGroundTaskWithNotif(…, error: true, …)` in the onlyDownload
 *        branch): that `downloadFailed` is not a failure and is ignored.
 *
 * 3e. Early access (`ota-early`) — the accounts listed in
 *    `module_config.languages.otaEarlyAccessEmails` (editable in the BO) get the
 *    live bundle as soon as it is published, whatever its rollout, so the team can
 *    try a production release on their own phones while the public is at 10 %. The
 *    backend tells the signed-in caller only its own flag (`otaEarlyAccess` in the
 *    `/api/auth/me` payload); `OtaBridge` (lib/native/OtaBridge.tsx, under the
 *    AuthProvider) calls `setOtaEarlyAccess()` on every auth change, and the literal
 *    part `ota-early` goes at the end of `custom_id`. The endpoint skips the rollout
 *    check for it and nothing else (plan § 3.3).
 *      - in memory only, false when this JavaScript starts: the launch's own
 *        `syncCustomId()` (in `initOtaUpdates`) rewrites the persisted copy
 *        without it, so a logged-out launch clears it as soon as the
 *        JavaScript runs; a signed-in launch gets it back once the session
 *        hydrates (the cold-start check, before any JavaScript, used the copy
 *        persisted by the previous session);
 *      - dropped on logout, on an account switch to an account that is not
 *        listed, and when the flag turns false (next `/api/auth/me`);
 *      - no UI, no toggle: it is server configuration, not app functionality.
 *
 * 4. Identity — Sentry tags `ota_bundle` (the running bundle version, `builtin` for
 *    the one inside the binary) and `native_build` (versionCode / CFBundleVersion).
 *    IDs only. The Sentry release already names the bundle (`speak@<sha>-ota-<platform>`
 *    vs `speak@<sha>-apk|ipa`, VITE_APP_VERSION).
 *
 * Everything is fail-open: any plugin error leaves the current bundle in
 * place, which is always a safe state. Plugins are reached through the bridge's
 * registry (`getPlugin` in lib/platform.ts), never by importing the package.
 */
import { getPlugin, isIOS } from '../platform.js';
import { sentryAddBreadcrumb, sentryCaptureMessage, sentrySetTags } from '../sentry.js';
import { analyticsRegisterDevice } from '../analytics.js';

/** Subset of the plugin's `BundleInfo` this module reads. */
export interface OtaBundleInfo {
  id: string;
  version: string;
  status?: string;
}

type UpdaterEvent =
  | { type: 'updateAvailable'; data: { bundle: OtaBundleInfo } }
  | { type: 'updateFailed'; data: { bundle: OtaBundleInfo } }
  | { type: 'download'; data: { percent: number; bundle: OtaBundleInfo } }
  | { type: 'downloadFailed'; data: { version: string; error?: string } }
  | { type: string; data?: unknown };

interface DelayCondition {
  kind: 'background' | 'kill' | 'nativeVersion' | 'date';
  value?: string;
}

/** A listener handle as the bridge registry returns it: the handle itself or a Promise of it. */
interface ListenerHandle {
  remove: () => Promise<void>;
}
type AddListenerResult = ListenerHandle | Promise<ListenerHandle>;

interface CapacitorUpdaterPlugin {
  notifyAppReady(): Promise<unknown>;
  current(): Promise<{ bundle: OtaBundleInfo; native?: string }>;
  list(): Promise<{ bundles: OtaBundleInfo[] }>;
  next(options: { id: string }): Promise<OtaBundleInfo>;
  getNextBundle(): Promise<OtaBundleInfo | null>;
  getFailedUpdate(): Promise<{ bundle: OtaBundleInfo } | null>;
  setMultiDelay(options: { delayConditions: DelayCondition[] }): Promise<void>;
  cancelDelay(): Promise<void>;
  setCustomId(options: { customId: string }): Promise<void>;
  /** Refused by the plugin for the current, builtin, next and fallback bundles. */
  delete?(options: { id: string }): Promise<void>;
  addListener(
    eventName: 'updaterEvent',
    listener: (event: UpdaterEvent) => void,
  ): AddListenerResult;
}

interface AppPlugin {
  getInfo(): Promise<{ build?: string }>;
}

const PLUGIN = 'CapacitorUpdater';
const BUILTIN_ID = 'builtin';
const REPORTED_FAILURE_KEY = 'orborix.ota.reported_failure';
const FAILED_VERSIONS_KEY = 'orborix.ota.failed_versions';
const DELTA_FAILURES_KEY = 'orborix.ota.delta_failures';
const DOWNLOADED_KEY = 'orborix.ota.downloaded';
/** Contract with the endpoint (apps/backend/src/lib/ota.ts `parseCustomId`, plan § 3.3). */
export const OTA_FAILED_CUSTOM_ID_PREFIX = 'ota-failed:';
export const OTA_ZIP_CUSTOM_ID_PREFIX = 'ota-zip:';
export const OTA_HAVE_CUSTOM_ID_PREFIX = 'ota-have:';
export const OTA_EARLY_CUSTOM_ID_PART = 'ota-early';
export const OTA_FAILED_MAX_VERSIONS = 5;
/** Delta failures of one version before the app asks for its zip (header 3c). */
export const DELTA_FAILURES_BEFORE_ZIP = 2;
/**
 * Android `downloadFailed.error` values of a per-file download that the plugin
 * refused BEFORE downloading anything, from the update answer alone
 * (DownloadService.handleManifestDownload, 8.53.0): a file hash not signed with
 * the embedded key, a bad path, a duplicate, or a missing session key. Retrying
 * cannot help, so the app asks for the zip at once.
 */
const DETERMINISTIC_DELTA_ERRORS: readonly string[] = [
  'Manifest contains invalid or duplicate file paths',
  'Session key required when public key is present',
];
/** OTA bundle version, `<major>.<minor>.<ci_run_number>` (plan § 3.4). */
const BUNDLE_VERSION_RE = /^\d{1,6}\.\d{1,6}\.\d{1,9}$/;
/** The hold: no automatic install while it is set; cleared at the next launch. */
const HOLD: DelayCondition[] = [{ kind: 'kill' }];
/**
 * `BundleInfo.status` of a bundle the plugin holds downloaded and usable: a
 * verified download is saved as `pending`; `success` is one that ran and
 * confirmed. `error`, `deleting`, `deleted` and `downloading` are not usable.
 */
const HELD_STATUSES: ReadonlySet<string> = new Set(['pending', 'success']);

function plugin(): CapacitorUpdaterPlugin | null {
  return getPlugin<CapacitorUpdaterPlugin>(PLUGIN);
}

// ---------------------------------------------------------------------------
// 1. notifyAppReady
// ---------------------------------------------------------------------------

let appReadyNotified = false;

/**
 * Confirms to the plugin that this bundle booted. Call once the React root
 * has committed its first render — never behind a network call. Idempotent,
 * never throws.
 */
export function markOtaAppReady(): void {
  if (appReadyNotified) return;
  const p = plugin();
  if (!p) return;
  appReadyNotified = true;
  try {
    void Promise.resolve(p.notifyAppReady()).catch(() => {
      // The plugin documents it never fails; if it does, the rollback is the
      // safe outcome and updateFailed will tell us.
    });
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// 2. When a bundle goes live
// ---------------------------------------------------------------------------

/** Downloaded and verified, not staged yet (a hold was taken). */
let pending: OtaBundleInfo | null = null;
/** Staged with next(): the plugin applies it on the next background. */
let staged: OtaBundleInfo | null = null;
/** Last hold state sent to the plugin; null = unknown (always re-send). */
let held: boolean | null = null;
/** Plugin calls go out one at a time, in order. */
let queue: Promise<void> = Promise.resolve();
/** Active holds (`holdOtaApply`), keyed by a token per call; value = reason. */
const holds = new Map<symbol, string>();

function schedule(task: () => Promise<void>): Promise<void> {
  queue = queue.then(task).catch((err: unknown) => {
    sentryAddBreadcrumb({
      category: 'ota',
      message: 'plugin call failed',
      level: 'warning',
      data: { error: err instanceof Error ? err.message : String(err) },
    });
  });
  return queue;
}

/**
 * THE APPLY DECISION — the only place that stages, holds or releases a
 * bundle. See the header (point 2) for the semantics it relies on.
 */
async function syncApply(p: CapacitorUpdaterPlugin): Promise<void> {
  if (!pending && !staged) return;

  if (holds.size > 0) {
    // A pending bundle stays pending; a staged one is held.
    if (staged && held !== true) {
      await p.setMultiDelay({ delayConditions: HOLD });
      held = true;
    }
    return;
  }

  if (pending) {
    const bundle = pending;
    await p.next({ id: bundle.id });
    staged = bundle;
    pending = null;
    held = null; // force the release below: the plugin may still carry an old hold
  }
  if (staged && held !== false) {
    await p.cancelDelay();
    held = false;
  }
}

function requestSync(): Promise<void> {
  const p = plugin();
  if (!p) return Promise.resolve();
  return schedule(() => syncApply(p));
}

/**
 * Holds every OTA apply until the returned `release()` is called: while any
 * hold is taken a downloaded bundle is not staged and a staged one is held with
 * the `kill` condition, so not even backgrounding the app reloads the WebView.
 * Use it around anything a reload must not cut in half — a voice session, a
 * purchase in flight (header, point 2).
 *
 * `reason` is a short id for breadcrumbs (`voice-session`). `release()` is
 * idempotent; forgetting to call it only delays the update to the next cold
 * start, never loses it. Works (and is a cheap no-op for the plugin) on web.
 */
export function holdOtaApply(reason: string): () => void {
  const token = Symbol(reason);
  holds.set(token, reason);
  if (holds.size === 1) void requestSync();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds.delete(token);
    if (holds.size === 0) void requestSync();
  };
}

/** Test/diagnostics: reasons of the holds currently taken. */
export function activeOtaHolds(): string[] {
  return [...holds.values()];
}

function onUpdateAvailable(bundle: OtaBundleInfo | undefined): void {
  if (!bundle?.id || bundle.id === BUILTIN_ID) return;
  sentryAddBreadcrumb({
    category: 'ota',
    message: 'bundle downloaded',
    data: { version: bundle.version },
  });
  pending = bundle;
  void requestSync();
  void requestPrune();
}

// ---------------------------------------------------------------------------
// 2b. Superseded downloads
// ---------------------------------------------------------------------------

/**
 * Deletes the downloads nothing will ever apply. A bundle downloaded while it could
 * not go live yet (a hold, or simply no background since) and then replaced by a
 * newer download stays on disk as `pending` forever: the plugin's
 * `autoDeletePrevious` only removes the bundle that was running before an apply,
 * no launch cleans it, and only a store update (`resetWhenUpdate`) would. Measured
 * on the Android emulator (plan 49 § 12): a whole copy of the web dir per version,
 * ~33 MB with `public/songs` (delta updates copy unchanged files from builtin into
 * each version's directory).
 *
 * Only `pending` bundles go — downloaded and verified, never run — and never the
 * running one, the staged one (`getNextBundle()`), nor the one this controller
 * keeps pending for after a hold. `success` bundles are the plugin's own business
 * (the fallback of a fresh apply), and `downloading` / `error` / `deleting` too.
 * The plugin refuses to delete the current, builtin, next and fallback bundles on
 * its own as well. Fail-open: a failed delete costs disk space, nothing else.
 */
async function pruneSuperseded(p: CapacitorUpdaterPlugin): Promise<void> {
  if (typeof p.delete !== 'function') return;
  const { bundle: current } = await p.current();
  const next = await p.getNextBundle().catch(() => null);
  const { bundles } = await p.list();
  const keep = new Set(
    [BUILTIN_ID, current?.id, next?.id, pending?.id, staged?.id].filter(
      (id): id is string => typeof id === 'string' && id !== '',
    ),
  );
  for (const b of bundles ?? []) {
    if (!b?.id || keep.has(b.id) || b.status !== 'pending') continue;
    try {
      await p.delete({ id: b.id });
      sentryAddBreadcrumb({
        category: 'ota',
        message: 'superseded download deleted',
        data: { version: b.version },
      });
    } catch {
      // fail-open: it stays on disk until the next prune or a store update
    }
  }
}

function requestPrune(): Promise<void> {
  const p = plugin();
  if (!p) return Promise.resolve();
  return schedule(() => pruneSuperseded(p));
}

// ---------------------------------------------------------------------------
// 3. Rollback reporting
// ---------------------------------------------------------------------------

function readReported(): string | null {
  try {
    return window.localStorage.getItem(REPORTED_FAILURE_KEY);
  } catch {
    return null;
  }
}

function writeReported(value: string): void {
  try {
    window.localStorage.setItem(REPORTED_FAILURE_KEY, value);
  } catch {
    // ignore — worst case the same rollback is reported twice
  }
}

/**
 * One Sentry issue per failed bundle version (the fingerprint groups every
 * event of a version into one issue). Exported for tests.
 *
 * Only the LAUNCH path marks a failure as reported. The `updateFailed` event
 * fires inside the failing bundle a few milliseconds before the plugin reloads
 * the WebView into the previous bundle (measured on Android, Club Arena plan § 8 test 3:
 * 3 ms), so its Sentry send is usually lost with the page. If the event path
 * marked the failure, the launch path — `getFailedUpdate()` in the bundle that
 * survived, which the plugin answers only once — would skip it and the
 * rollback would never reach Sentry. The cost of not marking: when the event
 * send does get out, the launch adds a second event to the same issue.
 */
export function reportUpdateFailed(
  bundle: OtaBundleInfo | undefined,
  source: 'event' | 'launch',
): void {
  if (!bundle?.id) return;
  const key = `${bundle.id}:${bundle.version}`;
  if (readReported() === key) return;
  if (source === 'launch') writeReported(key);
  sentryCaptureMessage('OTA bundle rolled back: it did not confirm notifyAppReady in time', {
    level: 'error',
    tags: { surface: 'ota', ota_failed_bundle: bundle.version, ota_failure_source: source },
    fingerprint: ['ota-update-failed', bundle.version],
  });
}

// ---------------------------------------------------------------------------
// 3b. Failed-version memory (custom_id)
// ---------------------------------------------------------------------------

/** Versions whose whole zip reached the disk in this session (see header 3b). */
const fullyDownloaded = new Set<string>();
/** Version of the bundle this page runs, once `current()` answered. */
let runningVersion: string | null = null;

/**
 * `download` progress at which the whole zip is on disk and verification
 * starts (plugin 8.53.0): Android emits 10–70 while bytes arrive (70 = "70 %
 * or more") and 71 right before decrypt + checksum; iOS emits 0, then 70 once
 * the download completed, then decrypts.
 */
function fullDownloadMark(): number {
  return isIOS() ? 70 : 71;
}

/** The list as stored: valid versions only, oldest first, at most 5. */
export function readFailedVersions(): string[] {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(FAILED_VERSIONS_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    const valid = raw.filter(
      (v): v is string => typeof v === 'string' && BUNDLE_VERSION_RE.test(v),
    );
    return [...new Set(valid)].slice(-OTA_FAILED_MAX_VERSIONS);
  } catch {
    return [];
  }
}

function writeFailedVersions(list: string[]): void {
  try {
    if (list.length === 0) window.localStorage.removeItem(FAILED_VERSIONS_KEY);
    else window.localStorage.setItem(FAILED_VERSIONS_KEY, JSON.stringify(list));
  } catch {
    // ignore — the native copy (custom_id) still holds the last list sent
  }
}

/**
 * THE `custom_id` format, built in this one place (the endpoint reads it in
 * one place too, apps/backend/src/lib/ota.ts `parseCustomId`, plan § 3.3): up to
 * four parts joined by `;`, each optional, always in this order —
 * `ota-failed:<v1>,<v2>…` (failed versions, 3b), `ota-zip:<v>` (the version
 * wanted as a zip, 3c), `ota-have:<v>` (the version downloaded and waiting,
 * 3d), `ota-early` (no value: the signed-in account has early access, 3e) —
 * or `''` (clears it). Versions and that one mark only; a version already
 * listed as failed is never also asked as a zip nor announced as held; junk is
 * dropped.
 */
export function otaCustomId(
  list: readonly string[],
  zipVersion: string | null = null,
  haveVersion: string | null = null,
  early = false,
): string {
  const usable = (v: string | null): v is string =>
    !!v && BUNDLE_VERSION_RE.test(v) && !list.includes(v);
  const parts: string[] = [];
  if (list.length > 0) parts.push(`${OTA_FAILED_CUSTOM_ID_PREFIX}${list.join(',')}`);
  if (usable(zipVersion)) parts.push(`${OTA_ZIP_CUSTOM_ID_PREFIX}${zipVersion}`);
  if (usable(haveVersion)) parts.push(`${OTA_HAVE_CUSTOM_ID_PREFIX}${haveVersion}`);
  if (early) parts.push(OTA_EARLY_CUSTOM_ID_PART);
  return parts.join(';');
}

function syncCustomId(list: readonly string[] = readFailedVersions()): Promise<void> {
  const p = plugin();
  if (!p) return Promise.resolve();
  const customId = otaCustomId(
    list,
    zipRequestedVersion(),
    readDownloaded()?.version ?? null,
    earlyAccess,
  );
  return schedule(() => p.setCustomId({ customId }));
}

// --- early access (header 3e) ---

/** The signed-in account has OTA early access. In memory only: false at every launch. */
let earlyAccess = false;

/**
 * The session's own `otaEarlyAccess` (false when signed out). Called by
 * `OtaBridge` on every auth change; rewrites `custom_id` only when the value
 * changes, and only once the controller runs (`initOtaUpdates` writes it with
 * the current value itself). Native only; never throws.
 */
export function setOtaEarlyAccess(on: boolean): void {
  const next = on === true;
  if (next === earlyAccess) return;
  earlyAccess = next;
  sentryAddBreadcrumb({ category: 'ota', message: next ? 'early access on' : 'early access off' });
  if (initialized) void syncCustomId();
}

/** The part of the auth state (`useAuth()` of @orborix/auth-client) early access reads. */
export interface OtaSessionState {
  loading: boolean;
  /**
   * The `/api/auth/me` user (`AuthUser` of @orborix/auth-client, whose
   * `otaEarlyAccess` is optional). Read defensively (`=== true`): a payload
   * without the field, or with anything but `true`, is no early access.
   */
  user: object | null;
}

/** True only for a signed-in session whose own payload says so. */
export function otaEarlyAccessOf(state: OtaSessionState): boolean {
  if (state.loading || state.user === null) return false;
  return (state.user as { otaEarlyAccess?: unknown }).otaEarlyAccess === true;
}

// --- delta failures (header 3c) ---

interface DeltaFailures {
  version: string;
  failures: number;
}

/** The delta-failure counter as stored (one version at a time), or null. */
export function readDeltaFailures(): DeltaFailures | null {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(DELTA_FAILURES_KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return null;
    const { version, failures } = raw as Partial<DeltaFailures>;
    if (typeof version !== 'string' || !BUNDLE_VERSION_RE.test(version)) return null;
    if (typeof failures !== 'number' || !Number.isInteger(failures) || failures < 1) return null;
    return { version, failures };
  } catch {
    return null;
  }
}

function writeDeltaFailures(value: DeltaFailures | null): void {
  try {
    if (value === null) window.localStorage.removeItem(DELTA_FAILURES_KEY);
    else window.localStorage.setItem(DELTA_FAILURES_KEY, JSON.stringify(value));
  } catch {
    // ignore — worst case the delta is retried a little more before the zip
  }
}

/** The version the app currently asks the endpoint to serve as a zip, if any. */
function zipRequestedVersion(): string | null {
  const state = readDeltaFailures();
  return state && state.failures >= DELTA_FAILURES_BEFORE_ZIP ? state.version : null;
}

/** Forgets the delta counter of `version` (it downloaded, runs, or failed for good). */
function clearDeltaFailures(version: string | undefined): boolean {
  const state = readDeltaFailures();
  if (!version || state?.version !== version) return false;
  writeDeltaFailures(null);
  return true;
}

/**
 * Counts a failed per-file download of `version`; a failure that cannot be
 * transient counts as enough on its own. Exported for tests.
 */
export function recordDeltaFailure(version: string | undefined, deterministic: boolean): void {
  if (!version || !BUNDLE_VERSION_RE.test(version)) return;
  const state = readDeltaFailures();
  const before = state?.version === version ? state.failures : 0;
  const failures = deterministic ? Math.max(before + 1, DELTA_FAILURES_BEFORE_ZIP) : before + 1;
  writeDeltaFailures({ version, failures });
  sentryAddBreadcrumb({
    category: 'ota',
    message: 'delta download failed',
    data: { version, failures, deterministic },
  });
  if (before < DELTA_FAILURES_BEFORE_ZIP && failures >= DELTA_FAILURES_BEFORE_ZIP) {
    sentryAddBreadcrumb({
      category: 'ota',
      message: 'asking the endpoint for the zip',
      data: { version },
    });
    void syncCustomId();
  }
}

/**
 * Remembers that `version` failed on this device and tells the plugin (and so
 * the endpoint). Moves an already listed version to the most recent slot.
 * Exported for tests.
 */
export function recordFailedVersion(
  version: string | undefined,
  cause: 'rollback' | 'rejected',
): void {
  if (!version || !BUNDLE_VERSION_RE.test(version)) return;
  const list = [...readFailedVersions().filter((v) => v !== version), version].slice(
    -OTA_FAILED_MAX_VERSIONS,
  );
  writeFailedVersions(list);
  clearDeltaFailures(version);
  forgetDownloaded(version);
  sentryAddBreadcrumb({
    category: 'ota',
    message: 'bundle version remembered as failed',
    data: { version, cause },
  });
  void syncCustomId(list);
}

function onDownloadProgress(data: { percent?: number; bundle?: OtaBundleInfo } | undefined): void {
  const version = data?.bundle?.version;
  if (typeof data?.percent !== 'number' || !version) return;
  if (data.percent >= fullDownloadMark()) fullyDownloaded.add(version);
}

/**
 * A download of `version` failed. Header 3b/3c: a ZIP rejected after a full
 * download is recorded as failed; a delta failure only counts towards asking
 * for the zip; a zip transport failure is retried.
 */
function onDownloadFailed(data: { version?: string; error?: string } | undefined): void {
  const version = data?.version;
  if (!version) return;
  const reachedMark = fullyDownloaded.delete(version);
  if (version === runningVersion) return;
  // Android re-announcing a version it already holds (header 3d): not a failure.
  // A zip rejected after its whole download is never this, so it still counts.
  if (!reachedMark && version === readDownloaded()?.version) return;
  const askedZip = zipRequestedVersion() === version;
  // A zip for sure: the app asked for it, or (Android) the attempt reached 71,
  // which a per-file download never emits before failing.
  if (askedZip || (!isIOS() && reachedMark)) {
    if (reachedMark) recordFailedVersion(version, 'rejected');
    return; // else: transport failure of the zip — retried on the next check
  }
  recordDeltaFailure(
    version,
    !isIOS() && typeof data?.error === 'string' && DETERMINISTIC_DELTA_ERRORS.includes(data.error),
  );
}

// ---------------------------------------------------------------------------
// 3d. Downloaded, waiting to apply (custom_id `ota-have:`)
// ---------------------------------------------------------------------------

interface DownloadedVersion {
  version: string;
  /** Native build (versionCode / CFBundleVersion) it was downloaded under, if known. */
  build: string | null;
}

/** Native build of this binary, once `App.getInfo()` answered. */
let nativeBuild: string | null = null;

/** The version the device holds downloaded and waiting, as stored, or null. */
export function readDownloaded(): DownloadedVersion | null {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(DOWNLOADED_KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return null;
    const { version, build } = raw as Partial<DownloadedVersion>;
    if (typeof version !== 'string' || !BUNDLE_VERSION_RE.test(version)) return null;
    return { version, build: typeof build === 'string' ? build : null };
  } catch {
    return null;
  }
}

function writeDownloaded(value: DownloadedVersion | null): void {
  try {
    if (value === null) window.localStorage.removeItem(DOWNLOADED_KEY);
    else window.localStorage.setItem(DOWNLOADED_KEY, JSON.stringify(value));
  } catch {
    // ignore — worst case the endpoint resends the update answer while it waits
  }
}

/** Remembers `bundle` as downloaded and waiting. Returns whether anything changed. */
function rememberDownloaded(bundle: OtaBundleInfo | undefined): boolean {
  const version = bundle?.version;
  if (!bundle?.id || bundle.id === BUILTIN_ID || !version || !BUNDLE_VERSION_RE.test(version))
    return false;
  if (version === runningVersion) return false;
  const before = readDownloaded();
  if (before?.version === version && before.build === nativeBuild) return false;
  writeDownloaded({ version, build: nativeBuild });
  return before?.version !== version;
}

/** Forgets the held version (all of it, or only if it is `version`). Returns whether it was set. */
function forgetDownloaded(version?: string): boolean {
  const held = readDownloaded();
  if (!held || (version !== undefined && held.version !== version)) return false;
  writeDownloaded(null);
  return true;
}

/**
 * Launch check of the held version (header 3d): kept only while the plugin
 * really holds it — same native build, not the running bundle, and a usable
 * bundle of that version in `list()`. Returns that bundle, or null (and forgets
 * the version). Any plugin error forgets it too: the cost of forgetting is one
 * full update answer, the cost of a stale one is a delayed update.
 */
async function reconcileDownloaded(p: CapacitorUpdaterPlugin): Promise<OtaBundleInfo | null> {
  const held = readDownloaded();
  if (!held) return null;
  const forget = (reason: string): null => {
    writeDownloaded(null);
    sentryAddBreadcrumb({
      category: 'ota',
      message: 'downloaded version no longer held',
      data: { version: held.version, reason },
    });
    return null;
  };
  if (held.build !== null && nativeBuild !== null && held.build !== nativeBuild)
    return forget('native_build_changed');
  try {
    const { bundle: current } = await p.current();
    if (current?.version === held.version && current.id !== BUILTIN_ID) return forget('running');
    const { bundles } = await p.list();
    const bundle = (bundles ?? []).find(
      (b) =>
        b?.version === held.version &&
        !!b.id &&
        b.id !== BUILTIN_ID &&
        HELD_STATUSES.has(b.status ?? ''),
    );
    return bundle ?? forget('not_in_list');
  } catch {
    return forget('plugin_error');
  }
}

// ---------------------------------------------------------------------------
// 4. Identity + init
// ---------------------------------------------------------------------------

async function readNativeBuild(): Promise<void> {
  try {
    const info = await getPlugin<AppPlugin>('App')?.getInfo();
    if (info?.build) nativeBuild = String(info.build);
  } catch {
    // leave it unknown
  }
}

async function tagIdentity(p: CapacitorUpdaterPlugin): Promise<void> {
  const tags: Record<string, string> = {};
  try {
    const { bundle } = await p.current();
    const builtin = !bundle?.id || bundle.id === BUILTIN_ID;
    tags.ota_bundle = builtin ? BUILTIN_ID : bundle.version;
    runningVersion = builtin ? null : bundle.version;
    // A version that runs is not waiting any more (header 3d).
    let changed = runningVersion !== null && forgetDownloaded(runningVersion);
    // A version that runs and confirmed (`success`) is not a failed one any more.
    if (runningVersion && bundle.status === 'success') {
      if (clearDeltaFailures(runningVersion)) changed = true;
      if (readFailedVersions().includes(runningVersion)) {
        writeFailedVersions(readFailedVersions().filter((v) => v !== runningVersion));
        changed = true;
      }
    }
    if (changed) void syncCustomId();
  } catch {
    // leave it out
  }
  if (nativeBuild) tags.native_build = nativeBuild;
  if (Object.keys(tags).length === 0) return;
  sentrySetTags(tags);
  // Same ids as PostHog super properties (lib/analytics.ts): which bundle an event ran on.
  analyticsRegisterDevice(tags);
}

let initialized = false;
let listener: AddListenerResult | null = null;

/**
 * Starts the controller: listens to the plugin, reconciles a bundle staged
 * in a previous session, reports a rollback from the previous launch and
 * tags the session. Call once from the bootstrap (main.tsx; a no-op on the
 * web). Idempotent, never throws. Returns once the initial work is queued and
 * done.
 */
export async function initOtaUpdates(): Promise<void> {
  if (initialized) return;
  const p = plugin();
  if (!p) return;
  initialized = true;

  try {
    listener = p.addListener('updaterEvent', (event) => {
      if (event.type === 'updateAvailable') {
        const bundle = (event.data as { bundle?: OtaBundleInfo } | undefined)?.bundle;
        if (bundle?.version) fullyDownloaded.delete(bundle.version);
        // Downloaded and verified (delta or zip): no need to ask for its zip any
        // more, and the endpoint can stop sending it (header 3d).
        const clearedDelta = clearDeltaFailures(bundle?.version);
        const remembered = rememberDownloaded(bundle);
        if (clearedDelta || remembered) void syncCustomId();
        onUpdateAvailable(bundle);
      } else if (event.type === 'updateFailed') {
        const bundle = (event.data as { bundle?: OtaBundleInfo } | undefined)?.bundle;
        recordFailedVersion(bundle?.version, 'rollback');
        reportUpdateFailed(bundle, 'event');
      } else if (event.type === 'download') {
        onDownloadProgress(event.data as { percent?: number; bundle?: OtaBundleInfo } | undefined);
      } else if (event.type === 'downloadFailed') {
        onDownloadFailed(event.data as { version?: string; error?: string } | undefined);
      }
    });
  } catch {
    listener = null;
  }

  await readNativeBuild();
  await schedule(async () => {
    const failed = await p.getFailedUpdate().catch(() => null);
    if (failed?.bundle) {
      recordFailedVersion(failed.bundle.version, 'rollback');
      reportUpdateFailed(failed.bundle, 'launch');
    }
  });
  // The version held downloaded (header 3d): still there, or forgotten.
  let heldBundle: OtaBundleInfo | null = null;
  await schedule(async () => {
    heldBundle = await reconcileDownloaded(p);
  });
  // Hand the stored state to the plugin on every launch, so its native copy
  // (persistCustomId) never drifts from ours — the cold-start check of the next
  // launch reads that copy before any JavaScript runs.
  await syncCustomId(readFailedVersions());
  await schedule(async () => {
    const next = await p.getNextBundle().catch(() => null);
    if (next?.id && next.id !== BUILTIN_ID && next.status !== 'error' && !staged && !pending) {
      staged = next;
      held = null;
    }
  });
  // A held bundle that is not the staged one waits as pending: the endpoint no
  // longer announces it, so nothing else would ever stage it.
  const restored = heldBundle as OtaBundleInfo | null;
  if (restored && !pending && staged?.version !== restored.version) pending = restored;
  await requestSync();
  // Leftovers of earlier sessions (2b): downloads a newer one replaced before they ran.
  await requestPrune();
  await tagIdentity(p);
}

/** Test-only: back to the state of a fresh page. */
export function resetOtaForTests(): void {
  appReadyNotified = false;
  initialized = false;
  pending = null;
  staged = null;
  held = null;
  queue = Promise.resolve();
  holds.clear();
  fullyDownloaded.clear();
  runningVersion = null;
  nativeBuild = null;
  earlyAccess = false;
  if (listener) {
    void Promise.resolve(listener)
      .then((h) => h.remove())
      .catch(() => {});
  }
  listener = null;
}
